import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import { login, register, forgotPassword } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRecaptcha } from "../hooks/useRecaptcha";

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { execute: executeRecaptcha, enabled: captchaEnabled } = useRecaptcha();

  const handleGoogle = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(email);
        setInfo("If that email exists, we've sent a password setup link. Please check your inbox.");
        setBusy(false);
        return;
      }
      let data;
      if (mode === "login") {
        data = await login(email, password);
      } else {
        const token = await executeRecaptcha("register");
        data = await register(email, password, name, token);
      }
      signIn(data.access_token, data.user);
      navigate(data.user.role === "admin" ? "/admin" : "/portal");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setErr("");
    setInfo("");
  };

  return (
    <div className="lm-font min-h-screen lm-hero-bg flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 lm-hero-grid opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-xl p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <Logo size={36} />
          <span className="lm-font font-extrabold text-[#11151C] text-lg">Live Mover</span>
        </Link>
        <h1 className="lm-font font-extrabold text-[#11151C] text-2xl">
          {mode === "login" && "Welcome back"}
          {mode === "register" && "Create your account"}
          {mode === "forgot" && "Reset your password"}
        </h1>
        <p className="text-[#11151C]/60 text-sm mt-1">
          {mode === "login" && "Sign in to manage your hosting"}
          {mode === "register" && "Start hosting with Live Mover"}
          {mode === "forgot" && "Enter your email and we'll send a secure link to set a new password."}
        </p>

        <button
          onClick={handleGoogle}
          className="mt-6 w-full bg-white border border-[#11151C]/15 hover:border-[#FF6B3D]/40 hover:bg-[#FFFBF6] rounded-xl py-3 flex items-center justify-center gap-3 transition-colors"
          type="button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          <span className="font-semibold text-[#11151C]">Continue with Google</span>
        </button>

        <div className="my-5 flex items-center gap-3 text-[#11151C]/40 text-xs">
          <div className="flex-1 h-px bg-[#11151C]/10" /> OR <div className="flex-1 h-px bg-[#11151C]/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-[#11151C]/15 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                required
              />
            </div>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#11151C]/40" />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full border border-[#11151C]/15 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
              required
            />
          </div>
          {mode !== "forgot" && (
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#11151C]/40" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full border border-[#11151C]/15 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                required
                minLength={6}
              />
            </div>
          )}

          {mode === "login" && (
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                data-testid="login-forgot-link"
                className="text-xs font-semibold text-[#FF6B3D] hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          {err && (
            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg p-2.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {err}
            </div>
          )}
          {info && (
            <div className="flex items-start gap-2 text-emerald-700 text-xs bg-emerald-50 border border-emerald-100 rounded-lg p-2.5" data-testid="forgot-success">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> {info}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="lm-btn-primary w-full py-3 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy
              ? "Please wait..."
              : mode === "login"
              ? "Sign in"
              : mode === "register"
              ? "Create account"
              : "Send reset link"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-[#11151C]/70">
          {mode === "register" && captchaEnabled && (
            <p className="text-[10px] text-[#11151C]/45 mb-3 leading-relaxed">
              Protected by reCAPTCHA — Google&apos;s{" "}
              <a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy</a> &{" "}
              <a href="https://policies.google.com/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms</a> apply.
            </p>
          )}
          {mode === "login" && (
            <>
              New to Live Mover?{" "}
              <button onClick={() => switchMode("register")} data-testid="switch-to-register" className="font-semibold text-[#FF6B3D] hover:underline">
                Create account
              </button>
            </>
          )}
          {mode === "register" && (
            <>
              Already have an account?{" "}
              <button onClick={() => switchMode("login")} data-testid="switch-to-login" className="font-semibold text-[#FF6B3D] hover:underline">
                Sign in
              </button>
            </>
          )}
          {mode === "forgot" && (
            <button
              onClick={() => switchMode("login")}
              data-testid="back-to-login"
              className="inline-flex items-center gap-1.5 font-semibold text-[#FF6B3D] hover:underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
